import React, { useMemo } from "react";
import { Table, Tag, Button, Tooltip, Space, Badge, Typography } from "antd";
import { EyeOutlined, UserSwitchOutlined, UserOutlined, SafetyOutlined } from "@ant-design/icons";
import moment from "moment";

const { Text } = Typography;

const LogsTable = ({ logs, loading, pagination, onChange, onView }) => {

  const getStatusTag = (status) => {
    const statusStr = String(status);
    let color = 'default';
    if (statusStr.startsWith('2')) color = 'success';
    if (statusStr.startsWith('4')) color = 'warning';
    if (statusStr.startsWith('5')) color = 'error';
    
    return <Tag color={color}>{status}</Tag>;
  };

  const getMethodTag = (method) => {
    const colors = {
      GET: 'cyan',
      POST: 'green',
      PUT: 'orange',
      PATCH: 'blue',
      DELETE: 'red'
    };
    return <Tag color={colors[method] || 'default'}>{method}</Tag>;
  };

  // Define columns with useMemo to prevent recreation on every render
  const columns = useMemo(() => [
    {
      title: "Timestamp",
      dataIndex: "created_at",
      render: (time) => (
        <Space direction="vertical" size={0}>
          <Text strong>{moment(time).format("YYYY-MM-DD")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{moment(time).format("HH:mm:ss")}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      width: 130,
    },
    {
      title: "User (Actor)",
      dataIndex: "actor_name",
      render: (name, record) => (
        <Space>
          <div style={{ 
            width: 32, 
            height: 32, 
            borderRadius: 16, 
            background: record.impersonated 
              ? "linear-gradient(135deg, #722ed1 0%, #531dab 100%)" 
              : "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white"
          }}>
            {record.impersonated ? <SafetyOutlined /> : <UserOutlined />}
          </div>
          <Space direction="vertical" size={0}>
            <Text strong>{record.actor_name}</Text>
            <Tag style={{ marginRight: 0 }}>{record.actor_role}</Tag>
            {record.impersonated && (
              <Badge status="processing" text="Impersonated" style={{ fontSize: 11 }} />
            )}
          </Space>
        </Space>
      ),
      width: 240,
    },
    {
      title: "Performed By",
      key: "performed_by",
      render: (_, record) => {
        // If impersonated, show the admin who performed it
        if (record.impersonated && record.performed_by_name) {
          return (
            <Space>
              <div style={{ 
                width: 32, 
                height: 32, 
                borderRadius: 16, 
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white"
              }}>
                <UserSwitchOutlined />
              </div>
              <Space direction="vertical" size={0}>
                <Text strong style={{ color: "#389e0d" }}>{record.performed_by_name}</Text>
                <Tag color="green">Admin</Tag>
              </Space>
            </Space>
          );
        }
        // If not impersonated, show "Self"
        else {
          return (
            <Space>
              <div style={{ 
                width: 32, 
                height: 32, 
                borderRadius: 16, 
                background: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#8c8c8c"
              }}>
                <UserOutlined />
              </div>
              <Space direction="vertical" size={0}>
                <Text type="secondary">{record.actor_name}</Text>
                <Tag color="default">Self</Tag>
              </Space>
            </Space>
          );
        }
      },
      width: 220,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (action, record) => (
        <Tag 
          color={record.impersonated ? "purple" : "blue"} 
          style={{ borderRadius: 12, padding: "2px 12px" }}
        >
          {action}
        </Tag>
      ),
      width: 120,
    },
    {
      title: "Endpoint",
      dataIndex: "endpoint",
      render: (endpoint) => (
        <Tooltip title={endpoint}>
          <Text style={{ maxWidth: 250 }} ellipsis>
            {endpoint}
          </Text>
        </Tooltip>
      ),
      ellipsis: true,
      width: 280,
    },
    {
      title: "Method",
      dataIndex: "method",
      render: (method) => getMethodTag(method),
      width: 100,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status_code",
      render: (status) => getStatusTag(status),
      width: 90,
      align: "center",
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      render: (ip) => (
        <Text style={{ fontFamily: "monospace" }}>{ip}</Text>
      ),
      width: 130,
    },
    {
      title: "",
      key: "view",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="primary"
            shape="circle"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onView(record);
            }}
            size="small"
            style={{ 
              backgroundColor: record.impersonated ? "#722ed1" : "#1890ff",
              borderColor: record.impersonated ? "#722ed1" : "#1890ff",
            }}
          />
        </Tooltip>
      ),
      width: 60,
      fixed: "right",
      align: "center",
    },
  ], []); // Empty dependency array means columns are created once

  // Calculate scroll x based on columns
  const getScrollX = () => {
    let totalWidth = 0;
    columns.forEach(col => {
      if (col.width) {
        totalWidth += typeof col.width === 'number' ? col.width : parseInt(col.width) || 0;
      }
    });
    return totalWidth + 50;
  };

  return (
    <Table
      rowKey={(record) => `${record.created_at}-${record.actor_user_id}-${record.action}`}
      columns={columns}
      dataSource={logs}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `Total ${total} items`,
        position: ["bottomRight"],
        style: { marginRight: 16 }
      }}
      onChange={onChange}
      scroll={{ x: getScrollX() }}
      style={{ marginTop: 8 }}
      bordered={false}
      size="middle"
    />
  );
};

export default LogsTable;